import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap'
import generatePDF from 'react-to-pdf'
import { t } from '../../locale'
import { inspectTransparenzCh, TransparenzChRecord } from '../../service/transparenzCh'
import TransparenzChResult from './TransparenzChResult'
import { TransparenzChResponse } from '../../types/transparenzregister-ch.type'
import { TransparenzChPdfContent } from './TransparenzChPdfContent'

interface TransparenzChModalProps {
  show: boolean
  clientId?: string
  clientName?: string
  clientUid?: string
  onClose: () => void
}

export default function TransparenzChModal({
  show,
  clientId,
  clientName,
  clientUid,
  onClose,
}: TransparenzChModalProps) {
  const [uid, setUid] = useState(clientUid || '')
  const [fiUid, setFiUid] = useState('')
  const [fiPid, setFiPid] = useState('')

  const [loading, setLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<TransparenzChRecord | null>(null)
  const pdfRef = useRef<HTMLDivElement>(null)
  const MAX_UPLOAD_SIZE_BYTES = 25 * 1024 * 1024

  useEffect(() => {
    if (!show) return

    setUid(clientUid || '')
    setFiUid('')
    setFiPid('')
    setError('')
    setResult(null)
  }, [show, clientUid])

  const handleInspect = async () => {
    if (!clientId || !uid.trim() || !fiUid.trim() || !fiPid.trim()) {
      setError('All fields are required.')
      return
    }

    try {
      setLoading(true)
      setError('')

      const response = await inspectTransparenzCh({
        uid: uid.trim(),
        clientId,
        fiUid: fiUid.trim(),
        fiPid: fiPid.trim(),
        languageCode: 'de',
      })

      // The backend surfaces credential / Key Manager problems as an error payload
      // ({ message }) without a record id - the global handler already shows the
      // toast, just don't render it as a result.
      if (!response || !response._id) {
        setError(t('transparenzChRequestFailed'))
        return
      }

      setResult(response)
    } catch (err) {
      console.error('Transparenz CH request failed:', err)

      setError('Swiss Transparency Register request failed.')
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadPdf = async () => {
    if (!clientId || !result) return

    const response = result.response as TransparenzChResponse
    const records = Array.isArray(response.boRecords) ? response.boRecords : []
    const mainName = records[0]?.recordStatements?.find(s => s.entityRecord)?.entityRecord?.name
    const baseName = (mainName || result.uid || 'transparenz-ch').replace(/[\\/:*?"<>|]/g, '_')
    const filename = `${baseName}.pdf`

    setPdfLoading(true)
    let pdfBlob: Blob
    try {
      const pdf = await generatePDF(pdfRef, { filename, page: { margin: 0, format: 'A4' } })
      pdfBlob = pdf.output('blob')

      if (!pdfBlob || pdfBlob.size === 0) throw new Error('Generated PDF is empty')

      if (pdfBlob.size > MAX_UPLOAD_SIZE_BYTES) {
        alert('File is too large to upload')
        return
      }
    } catch (err) {
      console.error('Error generating PDF:', err)
      alert('Error generating PDF')
      return
    } finally {
      setPdfLoading(false)
    }

    const formData = new FormData()
    formData.append('clientId', clientId)
    formData.append('type', 'transparenz-ch')
    formData.append('firmename', clientName || baseName)
    formData.append('file', pdfBlob, filename)

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/files/kyc/upload`, {
        method: 'POST',
        body: formData,
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(`Upload failed: ${res.status}. ${text}`)
      }
      await res.json()
      window.location.reload()
    } catch (err) {
      console.error('Error uploading PDF:', err)
      alert('Error uploading PDF to server')
    }
  }

  const handleNewCheck = () => {
    setResult(null)
    setUid('')
    setError('')
  }

  const handleClose = () => {
    setUid('')
    setFiUid('')
    setFiPid('')
    setResult(null)
    setError('')
    onClose()
  }

  return (
    <>
      {result && !Array.isArray(result.response) && (
        <TransparenzChPdfContent
          data={result.response as TransparenzChResponse}
          uid={result.uid}
          requestTimestamp={result.requestTimestamp}
          pdfRef={pdfRef}
        />
      )}
      <Modal show={show} onHide={handleClose} centered dialogClassName='modal-lg modern-modal'>
        <Modal.Header className='bg-gray-light' closeButton>
          <Modal.Title className='fs-5'>{t('transparenzChTitle')}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!result && (
            <>
              <Form.Group className='mb-3'>
                <Form.Label>{t('transparenzChCompanyUid')}</Form.Label>
                <Form.Control
                  value={uid}
                  placeholder='CHE-123.456.789'
                  onChange={event => setUid(event.target.value)}
                />
                <Form.Text muted>{t('transparenzChCompanyUidHint')}</Form.Text>
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label>{t('transparenzChFiUid')}</Form.Label>
                <Form.Control
                  value={fiUid}
                  placeholder='CHE123456789'
                  onChange={event => setFiUid(event.target.value)}
                />
                <Form.Text muted>{t('transparenzChFiUidHint')}</Form.Text>
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label>{t('transparenzChFiPid')}</Form.Label>
                <Form.Control
                  value={fiPid}
                  placeholder='Internal user ID'
                  maxLength={36}
                  onChange={event => setFiPid(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      handleInspect()
                    }
                  }}
                />
                <Form.Text muted>{t('transparenzChFiPidHint')}</Form.Text>
              </Form.Group>
            </>
          )}

          {error && <Alert variant='danger'>{error}</Alert>}

          {loading && (
            <div className='d-flex justify-content-center py-4'>
              <Spinner animation='border' />
            </div>
          )}

          {!loading && result && !Array.isArray(result.response) && (
            <>
              <div className='mb-3 small text-muted'>
                <strong>{t('transparenzChCheckedAt')}:</strong>{' '}
                {new Date(result.requestTimestamp).toLocaleString('de-CH')}
              </div>

              <TransparenzChResult
                data={result.response}
                clientName={clientName}
                clientUid={clientUid}
              />
            </>
          )}

          {!loading && result && Array.isArray(result.response) && (
            <Alert variant='warning'>{t('transparenzChNoResponse')}</Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          {result && !Array.isArray(result.response) && (
            <Button
              variant='outline-dark'
              size='sm'
              disabled={pdfLoading}
              onClick={handleDownloadPdf}
            >
              <i className='bi bi-file-earmark-pdf me-1' />
              {pdfLoading ? t('transparenzChGeneratingPdf') : t('transparenzChDownloadPdf')}
            </Button>
          )}
          {!result && (
            <Button
              variant='primary'
              disabled={loading || !clientId || !uid.trim() || !fiUid.trim() || !fiPid.trim()}
              onClick={handleInspect}
            >
              {t('transparenzChCheck')}
            </Button>
          )}

          {result && (
            <Button variant='outline-primary' onClick={handleNewCheck}>
              {t('transparenzChNewCheck')}
            </Button>
          )}

          <Button variant='secondary' onClick={handleClose}>
            {t('close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
